import React, {useMemo, useState} from 'react'
import {useFormik} from 'formik'
import {toast} from 'react-toastify'
import {ErrorMsg} from 'attendee/Auth/Register'
import {CountrySelect} from 'components/CountrySelect'
import {getCompanyUpdateSchema} from 'utils/validation-schema'
import {useI18n} from 'i18n/I18nContext'
import {countryCodeFromStored} from 'utils/countrySelect'
import {UpdateOrganizationCompanyData} from 'organization/providers/OrganizationProvider'
import {useAuth} from 'attendee/Auth/Providers/AuthProvider'

export default function AttendeeCompanyUpdate() {
  const {updateCompany, user} = useAuth()
  const [isBtnDisable, setBtnDisable] = useState(false)
  const {t} = useI18n()
  const companySchema = useMemo(() => getCompanyUpdateSchema(t), [t])

  const {handleSubmit, handleBlur, handleChange, values, errors, touched} =
    useFormik({
      enableReinitialize: true,
      initialValues: {
        company_name: user?.company_name ?? '',
        eik: user?.eik ?? '',
        vat_number: user?.vat_number ?? '', // keep as string in the form
        mol: user?.mol ?? '',
        country: countryCodeFromStored(user?.country),
        city: user?.city ?? '',
        postal_code: user?.postal_code ?? '',
        address_line: user?.address_line ?? '',
      },
      validationSchema: companySchema,
      onSubmit: async (values) => {
        setBtnDisable(true)

        const payload: UpdateOrganizationCompanyData = {
          ...values,
          country: values.country.toUpperCase(),
          vat_number:
            values.vat_number && values.vat_number.trim() !== ''
              ? values.vat_number.replace(/\s/g, '').toUpperCase()
              : null,
        }

        const result = await updateCompany(payload)
        if (result === true) toast.success(t('form.company.toastSaved'))
        setBtnDisable(false)
      },
    })

  return (
    <form onSubmit={handleSubmit}>
      <div className="singel__input-field mb-15">
        <label className="input__field-text">
          {t('form.company.nameLabel')}
        </label>
        <input
          name="company_name"
          type="text"
          placeholder={t('form.company.namePlaceholder')}
          value={values.company_name}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.company_name && <ErrorMsg errors={errors.company_name} />}
      </div>

      <div className="singel__input-field mb-15">
        <label className="input__field-text">
          {t('form.company.regNumberLabel')}
        </label>
        <input
          name="eik"
          type="text"
          placeholder={t('form.company.regNumberPlaceholder')}
          value={values.eik}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.eik && <ErrorMsg errors={errors.eik} />}
      </div>

      <div className="singel__input-field mb-15">
        <label className="input__field-text">
          {t('form.company.vatLabel')}
        </label>
        <input
          name="vat_number"
          type="text"
          placeholder={t('form.company.vatPlaceholder')}
          value={values.vat_number}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.vat_number && <ErrorMsg errors={errors.vat_number} />}
      </div>

      <div className="singel__input-field mb-15">
        <label className="input__field-text">
          {t('form.company.molLabel')}
        </label>
        <input
          name="mol"
          type="text"
          placeholder={t('form.company.molPlaceholder')}
          value={values.mol}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.mol && <ErrorMsg errors={errors.mol} />}
      </div>

      <div className="singel__input-field mb-15">
        <label className="input__field-text">
          {t('form.company.countryLabel')}
        </label>
        <CountrySelect
          name="country"
          value={values.country}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.country && <ErrorMsg errors={errors.country} />}
      </div>

      <div className="singel__input-field mb-15">
        <label className="input__field-text">
          {t('form.company.cityLabel')}
        </label>
        <input
          name="city"
          type="text"
          placeholder={t('form.company.cityPlaceholder')}
          value={values.city}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.city && <ErrorMsg errors={errors.city} />}
      </div>

      <div className="singel__input-field mb-15">
        <label className="input__field-text">
          {t('form.company.postalLabel')}
        </label>
        <input
          name="postal_code"
          type="text"
          placeholder={t('form.company.postalPlaceholder')}
          value={values.postal_code}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.postal_code && <ErrorMsg errors={errors.postal_code} />}
      </div>

      <div className="singel__input-field mb-15">
        <label className="input__field-text">
          {t('form.company.addressLabel')}
        </label>
        <input
          name="address_line"
          type="text"
          placeholder={t('form.company.addressPlaceholder')}
          value={values.address_line}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.address_line && <ErrorMsg errors={errors.address_line} />}
      </div>

      <div className="input__btn-wrapper">
        <button
          type="submit"
          disabled={isBtnDisable}
          className={
            isBtnDisable
              ? 'disableBtnStyle w-100 mb-20'
              : 'input__btn w-100 mb-20'
          }
        >
          {t('common.save')}
        </button>
      </div>
    </form>
  )
}
